import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { io } from "socket.io-client";
import { toast } from "sonner";
import {
  Broadcast, X, CornersOut, ChatCircleDots, PaperPlaneRight, Cursor,
  Monitor, WifiHigh, WifiSlash, ArrowLeft, Keyboard,
} from "@phosphor-icons/react";
import { SOCKET_URL, SOCKET_PATH } from "@/lib/api";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICE_SERVERS = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

export default function Session() {
  const { room } = useParams();
  const [params] = useSearchParams();
  const role = params.get("role") || "viewer";
  const connectionId = params.get("conn");
  const autoApprove = params.get("auto") !== "0"; // default ON
  const navigate = useNavigate();
  const { user } = useAuth();

  const socketRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const remoteCursorRef = useRef(null);

  const [status, setStatus] = useState("initializing");
  const [connected, setConnected] = useState(false);
  const [peerPresent, setPeerPresent] = useState(false);
  const [chatOpen, setChatOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [controlActive, setControlActive] = useState(false);
  const [pendingViewer, setPendingViewer] = useState(null); // {name} awaiting manual approval
  const [controllerName, setControllerName] = useState(null);
  const chatEndRef = useRef(null);
  const lastMoveRef = useRef(0);

  const isHost = role === "host";

  const emit = useCallback((event, data) => {
    if (socketRef.current?.connected) socketRef.current.emit(event, { room, ...data });
  }, [room]);

  const cleanup = useCallback(() => {
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (socketRef.current?.connected) socketRef.current.disconnect();
  }, []);

  const createPeer = useCallback(() => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    pc.onicecandidate = (e) => {
      if (e.candidate) emit("ice_candidate", { candidate: e.candidate });
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected") {
        setStatus("connected");
        toast.success("Peer connected");
      }
      if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
        if (isHost) {
          // Re-arm standby so the next connection is auto-served
          if (pcRef.current) { try { pcRef.current.close(); } catch (e) {} pcRef.current = null; }
          setControllerName(null);
          setControlActive(false);
          setStatus("waiting");
        } else {
          setStatus(pc.connectionState === "failed" ? "failed" : "disconnected");
        }
      }
    };
    if (!isHost) {
      pc.ontrack = (e) => {
        if (videoRef.current) videoRef.current.srcObject = e.streams[0];
        setStatus("connected");
      };
    }
    pcRef.current = pc;
    return pc;
  }, [emit, isHost]);

  // Host: create and send offer to a ready viewer
  const hostCreateOffer = useCallback(async () => {
    if (!localStreamRef.current) return;
    if (pcRef.current && pcRef.current.signalingState !== "closed") pcRef.current.close();
    const pc = createPeer();
    localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    emit("webrtc_offer", { sdp: offer });
    setStatus("calling");
  }, [createPeer, emit]);

  useEffect(() => {
    let mounted = true;

    const start = async () => {
      // Host: capture screen first
      if (isHost) {
        try {
          const stream = await navigator.mediaDevices.getDisplayMedia({
            video: { frameRate: 30 },
            audio: false,
          });
          if (!mounted) { stream.getTracks().forEach((t) => t.stop()); return; }
          localStreamRef.current = stream;
          // IMPORTANT: do NOT render the host's own captured screen back on this page.
          // Doing so creates an infinite "screen-in-screen" feedback loop when the host
          // is sharing its entire screen. The stream is only used to send over WebRTC.
          stream.getVideoTracks()[0].onended = () => { toast("Screen share stopped"); endSession(); };
          setStatus("waiting");
        } catch (e) {
          toast.error("Screen capture denied or unavailable");
          setStatus("failed");
          return;
        }
      } else {
        setStatus("waiting");
      }

      // Connect socket
      const socket = io(SOCKET_URL, { path: SOCKET_PATH, transports: ["websocket", "polling"] });
      socketRef.current = socket;

      socket.on("connect", () => {
        setConnected(true);
        emit("join_session", { role, name: user?.name || "Guest" });
        if (!isHost) emit("viewer_ready", {});
      });
      socket.on("disconnect", () => setConnected(false));

      socket.on("peer_joined", (d) => {
        setPeerPresent(true);
        if (isHost && d.role === "viewer") {
          if (autoApprove) {
            setControllerName(d.name);
            toast.success(`${d.name} connected (auto-approved)`);
            hostCreateOffer();
          } else {
            setPendingViewer({ name: d.name });
            toast(`${d.name} is requesting to connect`);
          }
        } else if (!isHost) {
          toast(`${d.name} joined`);
        }
      });
      socket.on("viewer_ready", () => {
        if (isHost && autoApprove) hostCreateOffer();
      });
      socket.on("peer_left", (d) => {
        setPeerPresent(false);
        toast(`${d.name || "Peer"} left`);
        if (isHost) {
          // Return to standby, ready to auto-accept the next connection
          if (pcRef.current) { try { pcRef.current.close(); } catch (e) {} pcRef.current = null; }
          setControllerName(null);
          setControlActive(false);
          setPendingViewer(null);
          setStatus("waiting");
        } else if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      });

      socket.on("webrtc_offer", async (d) => {
        if (isHost) return;
        const pc = pcRef.current || createPeer();
        await pc.setRemoteDescription(new RTCSessionDescription(d.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        emit("webrtc_answer", { sdp: answer });
      });

      socket.on("webrtc_answer", async (d) => {
        if (!isHost || !pcRef.current) return;
        await pcRef.current.setRemoteDescription(new RTCSessionDescription(d.sdp));
      });

      socket.on("ice_candidate", async (d) => {
        try { if (pcRef.current && d.candidate) await pcRef.current.addIceCandidate(new RTCIceCandidate(d.candidate)); }
        catch (e) {}
      });

      socket.on("chat_message", (m) => {
        setMessages((prev) => [...prev, m]);
      });

      socket.on("remote_control", (d) => {
        if (!isHost) return;
        setControlActive(true);
        const el = remoteCursorRef.current;
        const box = containerRef.current;
        if (el && box && d.type === "move") {
          const rect = box.getBoundingClientRect();
          el.style.left = `${d.x * rect.width}px`;
          el.style.top = `${d.y * rect.height}px`;
          el.style.opacity = "1";
        }
        if (d.type === "click") {
          const el2 = remoteCursorRef.current;
          if (el2) { el2.classList.add("scale-150"); setTimeout(() => el2.classList.remove("scale-150"), 150); }
        }
      });
    };

    start();
    return () => { mounted = false; cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const endSession = async () => {
    if (connectionId) { try { await api.post("/sessions/end", { connection_id: connectionId }); } catch (e) {} }
    cleanup();
    navigate("/dashboard");
  };

  const approveViewer = () => {
    if (!pendingViewer) return;
    setControllerName(pendingViewer.name);
    setPendingViewer(null);
    toast.success(`${pendingViewer.name} approved`);
    hostCreateOffer();
  };

  const denyViewer = () => {
    setPendingViewer(null);
    toast("Connection request denied");
    emit("chat_message", { text: "Connection request was denied by the host.", sender: "System" });
  };

  const sendChat = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    emit("chat_message", { text: chatInput.trim(), sender: user?.name || "Me" });
    setChatInput("");
  };

  // Viewer sends control events — mapped to the ACTUAL remote screen area,
  // accounting for object-contain letterboxing so the remote mouse lands correctly.
  const remotePoint = (e) => {
    const vid = videoRef.current;
    if (!vid) return null;
    const rect = vid.getBoundingClientRect();
    const vw = vid.videoWidth, vh = vid.videoHeight;
    if (!vw || !vh || !rect.width || !rect.height) return null;
    const scale = Math.min(rect.width / vw, rect.height / vh);
    const dispW = vw * scale, dispH = vh * scale;
    const offX = (rect.width - dispW) / 2, offY = (rect.height - dispH) / 2;
    const lx = e.clientX - rect.left - offX;
    const ly = e.clientY - rect.top - offY;
    if (lx < 0 || ly < 0 || lx > dispW || ly > dispH) return null; // pointer is on a black bar
    return { x: lx / dispW, y: ly / dispH };
  };

  const sendControl = (type, e) => {
    if (isHost) return;
    if (type === "move") {
      const now = performance.now();
      if (now - lastMoveRef.current < 40) return; // throttle to ~25Hz
      lastMoveRef.current = now;
    }
    const p = remotePoint(e);
    if (!p) return;
    emit("remote_control", { type, x: p.x, y: p.y });
  };

  const sendKey = (e) => {
    if (isHost) return;
    e.preventDefault();
    emit("remote_control", { type: "key", key: e.key });
  };

  const goFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  const statusColor = status === "connected" ? "text-[#34C759]" : status === "failed" ? "text-[#FF3B30]" : "text-[#FFCC00]";

  return (
    <div ref={containerRef} className="w-screen h-screen bg-black relative overflow-hidden" data-testid="session-view">
      {/* Remote screen video — VIEWER ONLY. The host must never render its own
          captured stream, or it creates an infinite screen-in-screen feedback loop. */}
      {!isHost && (
        <video
          ref={videoRef}
          autoPlay playsInline
          tabIndex={0}
          onMouseMove={(e) => sendControl("move", e)}
          onClick={(e) => sendControl("click", e)}
          onKeyDown={sendKey}
          className="w-full h-full object-contain bg-black cursor-none focus:outline-none"
          data-testid="remote-video"
        />
      )}

      {/* Remote cursor overlay (host sees viewer's cursor position) */}
      {isHost && (
        <div
          ref={remoteCursorRef}
          className="absolute pointer-events-none transition-transform duration-100"
          style={{ opacity: 0, transform: "translate(-50%,-50%)", left: 0, top: 0, zIndex: 30 }}
        >
          <Cursor size={26} weight="fill" className="text-[#0055FF] drop-shadow-lg" />
          <span className="font-mono text-[10px] bg-[#0055FF] text-white px-1.5 py-0.5 ml-3">remote</span>
        </div>
      )}

      {/* HOST CONSOLE — always shown for the host (we intentionally do not display the
          host's own screen here). Communicates standby/live state. */}
      {isHost && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0A0A0C] z-20 tech-grid" data-testid="host-console">
          {status === "connected" ? (
            <>
              <div className="w-3 h-3 bg-[#34C759] mb-6 animate-pulse" />
              <Broadcast size={48} weight="fill" className="text-[#0055FF] mb-4" />
              <p className="font-heading font-black text-2xl tracking-tighter">LIVE — this screen is being controlled</p>
              <p className="font-mono text-sm text-zinc-400 mt-2">Controlled by <span className="text-white">{controllerName || "a viewer"}</span></p>
              <p className="font-mono text-xs text-zinc-600 mt-4 max-w-md text-center leading-relaxed">
                Your entire screen is streaming to the viewer. You don't see a preview here on purpose — showing it would cause an infinite screen-in-screen loop.
              </p>
            </>
          ) : (
            <>
              <div className="loader-square mb-6" />
              <Monitor size={48} weight="thin" className="text-zinc-700 mb-4" />
              <p className="font-heading font-black text-2xl tracking-tighter">
                {status === "waiting" ? "STANDBY — ready for connections" : status === "calling" ? "Incoming viewer, negotiating..." : status === "failed" ? "Capture failed" : "Preparing screen..."}
              </p>
              <p className="font-mono text-sm text-zinc-500 mt-2">
                Room <span className="text-white">{room.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")}</span>
              </p>
              {status === "waiting" && (
                <>
                  <div className="mt-4 flex items-center gap-2 border border-zinc-800 px-3 py-1.5" data-testid="auto-approve-badge">
                    <span className={`w-1.5 h-1.5 ${autoApprove ? "bg-[#34C759]" : "bg-[#FFCC00]"}`} />
                    <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">
                      Auto-approve {autoApprove ? "ON" : "OFF"}
                    </span>
                  </div>
                  <p className="font-mono text-xs text-zinc-600 mt-4 max-w-sm text-center leading-relaxed">
                    {autoApprove
                      ? "Keep this tab open. Connections you start from your other PCs begin automatically — no clicks needed here."
                      : "Keep this tab open. You'll be asked to approve each incoming connection."}
                  </p>
                </>
              )}
              {status === "failed" && (
                <button onClick={endSession} className="mt-6 bg-white text-black font-mono text-sm uppercase tracking-widest px-6 py-3 hover:bg-zinc-200 transition-colors" data-testid="status-back-btn">
                  <ArrowLeft size={16} className="inline mr-2" /> Back to console
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Viewer waiting / status overlay */}
      {!isHost && status !== "connected" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0A0A0C] z-20 tech-grid" data-testid="session-status-overlay">
          <div className="loader-square mb-6" />
          <Monitor size={48} weight="thin" className="text-zinc-700 mb-4" />
          <p className="font-heading font-black text-2xl tracking-tighter">
            {status === "waiting" ? "Connecting to host screen..." : status === "failed" ? "Connection failed" : "Establishing stream..."}
          </p>
          <p className="font-mono text-sm text-zinc-500 mt-2">
            Room <span className="text-white">{room.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")}</span>
          </p>
          {status === "failed" && (
            <button onClick={endSession} className="mt-6 bg-white text-black font-mono text-sm uppercase tracking-widest px-6 py-3 hover:bg-zinc-200 transition-colors" data-testid="status-back-btn">
              <ArrowLeft size={16} className="inline mr-2" /> Back to console
            </button>
          )}
        </div>
      )}

      {/* Top floating toolbar */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-black/60 backdrop-blur-2xl border border-white/10 shadow-2xl px-3 py-2" data-testid="session-toolbar">
        <span className="flex items-center gap-2 px-2">
          {isHost ? <Broadcast size={16} className="text-[#0055FF]" weight="fill" /> : <Monitor size={16} className="text-[#00E5FF]" />}
          <span className="font-mono text-[11px] uppercase tracking-widest text-white" data-testid="session-mode-label">
            {isHost ? (status === "connected" ? `Live · ${controllerName || "controlled"}` : "Standby") : "Viewing"}
          </span>
        </span>
        <span className="w-px h-5 bg-white/10" />
        <span className={`flex items-center gap-1.5 px-2 font-mono text-[11px] ${statusColor}`}>
          {connected ? <WifiHigh size={15} /> : <WifiSlash size={15} />} {status}
        </span>
        <span className="w-px h-5 bg-white/10" />
        <button onClick={() => setChatOpen((v) => !v)} className="p-1.5 hover:bg-white/10 transition-colors" data-testid="toggle-chat-btn" title="Chat">
          <ChatCircleDots size={18} className="text-white" />
        </button>
        <button onClick={goFullscreen} className="p-1.5 hover:bg-white/10 transition-colors" data-testid="fullscreen-btn" title="Fullscreen">
          <CornersOut size={18} className="text-white" />
        </button>
        <button onClick={endSession} className="p-1.5 hover:bg-[#FF3B30]/20 transition-colors" data-testid="end-session-btn" title="Disconnect">
          <X size={18} className="text-[#FF3B30]" />
        </button>
      </div>

      {/* Control-active badge for viewer */}
      {!isHost && status === "connected" && (
        <div className="absolute bottom-4 left-4 z-40 flex items-center gap-2 bg-black/60 backdrop-blur-2xl border border-white/10 px-3 py-2" data-testid="control-hint">
          <Keyboard size={16} className="text-[#00E5FF]" />
          <span className="font-mono text-[11px] text-zinc-300">Move & click on the screen to control · click video for keyboard</span>
        </div>
      )}
      {isHost && controlActive && (
        <div className="absolute bottom-4 left-4 z-40 flex items-center gap-2 bg-black/60 backdrop-blur-2xl border border-[#0055FF]/40 px-3 py-2">
          <Cursor size={16} className="text-[#0055FF]" />
          <span className="font-mono text-[11px] text-zinc-300">Remote control active</span>
        </div>
      )}

      {/* Approval prompt (when auto-approve is OFF) */}
      {isHost && pendingViewer && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="approve-modal">
          <div className="w-full max-w-md bg-[#121214] border border-zinc-800 p-8">
            <div className="flex items-center gap-2 mb-2"><Cursor size={18} className="text-[#0055FF]" /><span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Incoming Connection</span></div>
            <p className="text-sm text-zinc-300 mb-6"><span className="text-white font-mono">{pendingViewer.name}</span> wants to view and control this screen.</p>
            <div className="flex gap-3">
              <button onClick={denyViewer} className="flex-1 border border-zinc-700 py-3 font-mono text-sm uppercase tracking-widest hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors" data-testid="deny-viewer-btn">Deny</button>
              <button onClick={approveViewer} className="flex-1 bg-[#0055FF] text-white py-3 font-mono text-sm uppercase tracking-widest hover:bg-blue-700 transition-colors" data-testid="approve-viewer-btn">Approve</button>
            </div>
          </div>
        </div>
      )}

      {/* Chat panel */}
      {chatOpen && (
        <div className="absolute top-0 right-0 h-full w-80 bg-black/70 backdrop-blur-2xl border-l border-white/10 z-40 flex flex-col" data-testid="chat-panel">
          <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
            <span className="font-mono text-[11px] uppercase tracking-widest text-white">Session Chat</span>
            <button onClick={() => setChatOpen(false)} className="text-zinc-400 hover:text-white" data-testid="close-chat-btn"><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && <p className="font-mono text-xs text-zinc-600">No messages yet.</p>}
            {messages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.sender === (user?.name || "Me") ? "items-end" : "items-start"}`} data-testid="chat-message">
                <span className="font-mono text-[10px] text-zinc-500 mb-0.5">{m.sender}</span>
                <span className={`text-sm px-3 py-2 max-w-[85%] ${m.sender === (user?.name || "Me") ? "bg-[#0055FF] text-white" : "bg-zinc-800 text-white"}`}>{m.text}</span>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={sendChat} className="p-3 border-t border-white/10 flex gap-2">
            <input
              data-testid="chat-input"
              value={chatInput} onChange={(e) => setChatInput(e.target.value)}
              placeholder="Message..."
              className="flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-white focus:border-[#0055FF] outline-none transition-colors"
            />
            <button type="submit" data-testid="send-chat-btn" className="bg-[#0055FF] hover:bg-blue-700 transition-colors px-3"><PaperPlaneRight size={16} weight="fill" /></button>
          </form>
        </div>
      )}
    </div>
  );
}
